import { FC, useEffect } from 'react'
import { useFormikContext } from 'formik'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { Configuration } from '@/api'
import { getFieldTestId } from '@/utils'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { NfField } from '../primitives/NfField'
import { nfSelectMenuProps } from '../nfFieldSx'
import ExportTypeDisplayChip from '@/components/ExportTypeDisplayChip'
import { ConfigurationType, ExportType } from '@/models'
import { SHOW_CONFIGURATION_NAMES } from '@/constants'
import { getExportTypeAndConfigurationType } from '@/utils/getExportTypeAndConfigurationType'

export interface DeclarationTypeFieldProps {
  configurations: Configuration[]
}

/**
 * Type de déclaration — requis, pré-rempli avec la première configuration
 * disponible pour l'agence sélectionnée (cf. desktop/design_handoff_wizard).
 * Le champ se réinitialise quand l'agence change (voir AgencyField).
 */
export const DeclarationTypeField: FC<DeclarationTypeFieldProps> = ({
  configurations,
}) => {
  const formik = useFormikContext<DialogUploadValues>()
  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')

  const currentCid = formik.values.exportConfiguration
  const selectedOption = configurations.find((c) => c.cid === currentCid)
  const selectValue = selectedOption?.cid ?? ''
  const nextCid = configurations[0]?.cid ?? ''

  // Pré-remplissage / resync en préparation classique uniquement.
  // En intelligente le type reste vide tant que l'utilisateur ne le choisit pas.
  useEffect(() => {
    if (formik.values.agentic) return
    if (currentCid !== nextCid && !selectedOption) {
      formik.setFieldValue('exportConfiguration', nextCid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCid, nextCid, selectedOption, formik.values.agentic])

  const hasError =
    formik.touched.exportConfiguration && Boolean(formik.errors.exportConfiguration)

  return (
    <NfField
      label={tUpload('declarationType')}
      required={!formik.values.agentic}
      error={hasError ? (formik.errors.exportConfiguration as string) : null}
      tooltip={tUpload('wizardTypeTooltip')}
      disabledReason={
        configurations.length === 0 ? tUpload('wizardChooseGroupFirst') : null
      }
    >
      <Select
        displayEmpty
        disabled={configurations.length === 0}
        error={hasError}
        renderValue={(value) => {
          if (!value) return tUpload('wizardTypePlaceholder')
          const option = configurations.find((c) => c.cid === value)
          if (!option) return tUpload('wizardTypePlaceholder')
          return (
            <ExportTypeDisplayChip
              exportType={option.decTypeName as ExportType}
              exportTypeFullName={getExportTypeAndConfigurationType(
                option.decTypeName as ExportType,
                option.name as ConfigurationType,
                SHOW_CONFIGURATION_NAMES
              )}
            />
          )
        }}
        MenuProps={nfSelectMenuProps}
        inputProps={{ ['data-testid']: 'export-configuration-input' }}
        {...formik.getFieldProps('exportConfiguration')}
        value={selectValue}
      >
        {configurations.length > 0 ? (
          configurations.map((option, index) => (
            <MenuItem
              key={option.cid}
              value={option.cid}
              {...getFieldTestId(`export-configuration-${index}`)}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <ExportTypeDisplayChip
                  exportType={option.decTypeName as ExportType}
                  exportTypeFullName={getExportTypeAndConfigurationType(
                    option.decTypeName as ExportType,
                    option.name as ConfigurationType,
                    SHOW_CONFIGURATION_NAMES
                  )}
                />
              </Stack>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled value="">
            {tCommon('noOptionAvailable')}
          </MenuItem>
        )}
      </Select>
    </NfField>
  )
}

export default DeclarationTypeField
